import { BRAND, BRAND_LONG } from "@/lib/brand";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Building2, LayoutDashboard, LogOut, ShieldCheck, Globe2, Stethoscope,
  Users, BookOpen } from "lucide-react";

const PLATFORM_NAV = [
  { to: "/platform", label: "Overview", icon: LayoutDashboard, testid: "nav-platform-overview" },
  { to: "/platform/hospitals", label: "Hospitals", icon: Building2, testid: "nav-platform-hospitals" },
  { to: "/platform/admins", label: "Hospital Admins", icon: ShieldCheck, testid: "nav-platform-admins" },
  { to: "/platform/users", label: "Users", icon: Users, testid: "nav-platform-users" },
  { to: "/platform/doctors", label: "Doctors", icon: Stethoscope,
  Users, testid: "nav-platform-doctors" },
  { to: "/platform/manual", label: "User Manual", icon: BookOpen, testid: "nav-platform-manual" },
];

export default function PlatformLayout() {
  const { user, loading, logout } = useAuth();
  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="app-aside no-print w-64 shrink-0 bg-slate-900 text-slate-100 border-r border-slate-800 flex flex-col" data-testid="platform-sidebar">
        <div className="p-5 border-b border-slate-800 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-indigo-500 text-white flex items-center justify-center">
            <Globe2 size={18} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold leading-none" style={{ fontFamily: "Work Sans" }}>{BRAND}</div>
            <div className="text-xs text-indigo-300 mt-1">Platform Console</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {PLATFORM_NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/platform"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-indigo-500/20 text-white font-medium"
                      : "text-slate-300 hover:bg-slate-800"
                  }`
                }
              >
                <Icon size={16} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-medium text-white truncate" data-testid="platform-user-name">{user?.name || "Platform Admin"}</div>
            <div className="text-xs text-slate-400 truncate">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-wider text-indigo-300 mt-1">{user?.role}</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start bg-transparent text-slate-100 border-slate-700 hover:bg-slate-800"
            onClick={logout}
            data-testid="btn-platform-logout"
          >
            <LogOut size={14} className="mr-2" /> Logout
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
